import React, { useEffect, useState } from 'react';
import {
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Box,
    Divider,
    Typography,
    IconButton,
    useTheme,
    Tooltip,
    Avatar,
    Stack,
    Chip,
    CircularProgress
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ThemeModeToggle from './ThemeModeToggle';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';
import {
    PlayArrow as StartIcon,
    History as HistoryIcon,
    Description as ReportsIcon,
    Settings as SettingsIcon,
    Person as ProfileIcon,
    Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import ScanQuota from './ScanQuota';  // Make sure path is correct
import scanApi from '../../services/api';
import SidebarToggle from './SidebarToggle';
import { Link, useLocation } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DomainVerificationIcon from '@mui/icons-material/DomainVerification';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'; // Add this import
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import LogoutIcon from '@mui/icons-material/Logout';  // Add this import
import DnsIcon from '@mui/icons-material/Dns';  // Add this import

const mainMenuItems = [
    { path: '/scan', icon: <SearchIcon />, label: 'Start New Scan' },
    { path: '/history', icon: <HistoryIcon />, label: 'Scan History' },
    { path: '/reports', icon: <AssessmentIcon />, label: 'Reports' },
    { path: '/domains', icon: <DomainVerificationIcon />, label: 'Domain Verification' },
    { 
        path: '/subdomains', 
        icon: <DnsIcon />, 
        label: 'SubRecon',
        primary: true 
    }
];

const secondaryMenuItems = [
    { path: '/settings', icon: <SettingsIcon />, label: 'Settings' }
];

const Sidebar = ({ onNavigate, currentPath, mobileOpen, onDrawerToggle, isMobile, isOpen, onToggle }) => {
    const theme = useTheme();
    const { mode, toggleTheme } = useCustomTheme();
    const { user, quotaInfo, loading, refreshUserInfo } = useAuth();
    const [anchorEl, setAnchorEl] = useState(null);

    useEffect(() => {
        const loadUserData = async () => {
            if (!user && !loading) {
                try {
                    await refreshUserInfo();
                } catch (error) {
                    console.error('Failed to load user data:', error);
                }
            }
        };
        
        loadUserData();
    }, [user, loading, refreshUserInfo]);

    const clearLocalStorageExceptAuth = () => {
        const token = localStorage.getItem('token');
        localStorage.clear();
        if (token) {
            localStorage.setItem('token', token);
        }
    };

    const handleNavigation = (path) => {
        if (onNavigate) {
            // Previeni la navigazione se siamo già sulla pagina corrente
            if (path === currentPath) {
                return;
            }
            
            // Clear localStorage except auth token
            clearLocalStorageExceptAuth();
            
            onNavigate(path);
            if (isMobile && onDrawerToggle) {
                onDrawerToggle();
            }
            
            // Force page refresh
            window.location.href = path;
        }
    };

    const handleLogout = () => {
        scanApi.logout();
        // Additional logout logic if needed
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const renderUserInfo = () => {
        if (loading) {
            return (
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={24} />
                </Box>
            );
        }

        if (!user) return null;

        const initials = user.name
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase();

        return isOpen ? (
            <Stack spacing={1} sx={{ p: 2 }}>
                <Stack 
                    direction="row" 
                    spacing={2} 
                    alignItems="center"
                    onClick={handleMenuOpen}
                    sx={{ cursor: 'pointer' }}
                >
                    <Avatar sx={{ 
                        bgcolor: 'primary.main',
                        width: 40,
                        height: 40,
                        fontSize: '1.2rem'
                    }}>
                        {initials}
                    </Avatar>
                    <Stack>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {user.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {quotaInfo ? `${quotaInfo.remaining}/${quotaInfo.total} scans left` : 'Loading quota...'}
                        </Typography>
                    </Stack>
                </Stack>
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <MenuItem onClick={() => {
                        handleMenuClose();
                        handleNavigation('/profile');
                    }}>
                        <ListItemIcon>
                            <AccountCircleIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Profile</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>
                        <ListItemIcon>
                            <LogoutIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Sign out</ListItemText>
                    </MenuItem>
                </Menu>
            </Stack>
        ) : (
            <Tooltip title={`${user.name} - ${quotaInfo ? `${quotaInfo.remaining} scans left` : 'Loading...'}`} placement="right">
                <Avatar sx={{ 
                    width: 32,
                    height: 32,
                    bgcolor: 'primary.main',
                    fontSize: '0.9rem'
                }}>
                    {initials}
                </Avatar>
            </Tooltip>
        );
    };

    const drawer = (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            height: '100%',
            position: 'relative',
            pt: '64px' // Add top padding equal to AppBar height
        }}>
            <SidebarToggle 
                isOpen={isOpen} 
                onClick={onToggle}
                sx={{
                    display: { xs: 'none', sm: 'flex' },
                    position: 'absolute',
                    right: -20,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: theme.zIndex.drawer + 2
                }}
            />

            <ScanQuota quotaInfo={quotaInfo} isOpen={isOpen} />
            <Divider />
            <List sx={{ flexGrow: 1 }}>
                {mainMenuItems.map((item) => (
                    <Tooltip 
                        title={!isOpen ? item.label : ''} 
                        placement="right" 
                        key={item.path}
                    >
                        <ListItem
                            button
                            onClick={() => handleNavigation(item.path)}
                            selected={currentPath === item.path}
                            sx={{
                                minHeight: 48,
                                px: 2.5,
                                mb: 1,
                                mx: 1,
                                borderRadius: 1,
                                justifyContent: isOpen ? 'initial' : 'center',
                                '&.Mui-selected': {
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    '& .MuiListItemIcon-root': {
                                        color: 'inherit'
                                    },
                                    '&:hover': {
                                        bgcolor: 'primary.dark',
                                    }
                                }
                            }}
                        >
                            <ListItemIcon sx={{ 
                                minWidth: 0, 
                                mr: isOpen ? 2 : 'auto',
                                justifyContent: 'center',
                                color: currentPath === item.path ? 'inherit' : 'text.secondary'
                            }}>
                                {item.icon}
                            </ListItemIcon>
                            {isOpen && (
                                <ListItemText 
                                    primary={item.label}
                                    primaryTypographyProps={{
                                        fontWeight: item.primary ? 600 : 400
                                    }}
                                />
                            )}
                        </ListItem>
                    </Tooltip>
                ))}

                {quotaInfo && quotaInfo.remaining === 0 && (
                    <Tooltip title={!isOpen ? 'Purchase Scans' : ''} placement="right">
                        <ListItem
                            button
                            onClick={() => handleNavigation('/plans')}
                            sx={{
                                minHeight: 48,
                                px: 2.5,
                                mb: 1,
                                mx: 1,
                                borderRadius: 1,
                                justifyContent: isOpen ? 'initial' : 'center',
                                '&.Mui-selected': {
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    '& .MuiListItemIcon-root': {
                                        color: 'inherit'
                                    },
                                    '&:hover': {
                                        bgcolor: 'primary.dark',
                                    }
                                }
                            }}
                        >
                            <ListItemIcon sx={{ 
                                minWidth: 0, 
                                mr: isOpen ? 2 : 'auto',
                                justifyContent: 'center',
                            }}>
                                <ShoppingCartIcon />
                            </ListItemIcon>
                            {isOpen && (
                                <ListItemText 
                                    primary="Purchase Scans"
                                    primaryTypographyProps={{
                                        fontWeight: 600,
                                        color: 'primary'
                                    }}
                                />
                            )}
                        </ListItem>
                    </Tooltip>
                )}

                <Divider sx={{ my: 2 }} />

                {secondaryMenuItems.map((item) => (
                    <Tooltip 
                        title={!isOpen ? item.label : ''} 
                        placement="right" 
                        key={item.path}
                    >
                        <ListItem
                            button
                            onClick={() => handleNavigation(item.path)}
                            selected={currentPath === item.path}
                            sx={{
                                minHeight: 48,
                                px: 2.5,
                                mb: 1,
                                mx: 1,
                                borderRadius: 1,
                                justifyContent: isOpen ? 'initial' : 'center',
                                '&.Mui-selected': {
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    '& .MuiListItemIcon-root': {
                                        color: 'inherit'
                                    },
                                    '&:hover': {
                                        bgcolor: 'primary.dark',
                                    }
                                }
                            }}
                        >
                            <ListItemIcon sx={{ 
                                minWidth: 0, 
                                mr: isOpen ? 2 : 'auto',
                                justifyContent: 'center',
                                color: currentPath === item.path ? 'inherit' : 'text.secondary'
                            }}>
                                {item.icon}
                            </ListItemIcon>
                            {isOpen && (
                                <ListItemText 
                                    primary={item.label}
                                    primaryTypographyProps={{
                                        fontWeight: item.primary ? 600 : 400
                                    }}
                                />
                            )}
                        </ListItem>
                    </Tooltip>
                ))}
            </List>
        </Box>
    );

    return (
        <Box
            component="nav"
            sx={{
                width: { sm: isOpen ? 240 : 73 },
                flexShrink: { sm: 0 },
                transition: theme => theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                }),
                position: 'relative', // Importante per il posizionamento della freccia
                '&:hover .toggle-button': {
                    opacity: 1
                }
            }}
        >
            {isMobile && (
                <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={onDrawerToggle}
                    sx={{ 
                        mr: 2, 
                        display: { sm: 'none' },
                        position: 'fixed',
                        top: 10,
                        left: 10,
                        zIndex: theme.zIndex.drawer + 2
                    }}
                >
                    <MenuIcon />
                </IconButton>
            )}
            
            {/* Mobile drawer */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={onDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', sm: 'none' },
                    '& .MuiDrawer-paper': { 
                        width: 240,
                        transition: theme => theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        })
                    },
                }}
            >
                {drawer}
            </Drawer>

            {/* Desktop drawer */}
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: 'none', sm: 'block' },
                    '& .MuiDrawer-paper': {
                        width: isOpen ? 240 : 73,
                        overflow: 'hidden',
                        transition: theme => theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                        bgcolor: 'background.sidebar',
                        borderRight: '1px solid',
                        borderColor: 'divider'
                    },
                }}
                open={isOpen}
            >
                {drawer}
            </Drawer>
        </Box>
    );
};

export default Sidebar;
